'use client'

import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'

import type { AssistantSkill } from '~/data/api/client'

export type RunSkillRequest = {
  skill: AssistantSkill
  message?: string
  newChat?: boolean
}

type AssistantRunSkillContextValue = {
  pendingRun: RunSkillRequest | null
  runSkill: (skill: AssistantSkill, options?: { message?: string; newChat?: boolean }) => void
  acknowledgePendingRun: () => void
}

const AssistantRunSkillContext = createContext<AssistantRunSkillContextValue | null>(null)

export function AssistantRunSkillProvider({ children }: PropsWithChildren) {
  const [pendingRun, setPendingRun] = useState<RunSkillRequest | null>(null)

  const runSkill = useCallback((skill: AssistantSkill, options?: { message?: string; newChat?: boolean }) => {
    setPendingRun({
      skill,
      message: options?.message ?? '',
      newChat: options?.newChat ?? true
    })
  }, [])

  const acknowledgePendingRun = useCallback(() => {
    setPendingRun(null)
  }, [])

  const value = useMemo(
    () => ({ pendingRun, runSkill, acknowledgePendingRun }),
    [acknowledgePendingRun, pendingRun, runSkill]
  )

  return <AssistantRunSkillContext.Provider value={value}>{children}</AssistantRunSkillContext.Provider>
}

export function useAssistantRunSkill(): AssistantRunSkillContextValue {
  const context = useContext(AssistantRunSkillContext)
  if (!context) {
    throw new Error('useAssistantRunSkill must be used within AssistantRunSkillProvider')
  }
  return context
}
